import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import ko from "knockout";
import AppConfig from "AppConfig";
import "./ReportRenderer.css";
import { ActionId, TimeOut as ViewerTimeOut } from 'devexpress-reporting/dx-webdocumentviewer';
import { useFlags } from "launchdarkly-react-client-sdk";
import useManagedAuth from "../../hooks/useManagedAuth";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { useSelector, shallowEqual } from "react-redux";
import { USER_TYPES } from "../../constants/index";
import { ajaxSetup } from "@devexpress/analytics-core/analytics-utils";
import { useHistory } from 'react-router-dom';
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import apiClient from "../../auth/apiClient";
import ThemeProvider from "../../providers/ThemeProvider";
import ReportQueueService from "../../services/ReportQueueService";
import ReportQueueControlsWrapper from "./ReportQueueControlsWrapper";
import ReportV2RoutingWrapper from "./ReportV2RoutingWrapper";

const ReportRenderer = ({ className, reportName, reportQueueEnabled, reportQueueV2Enabled }) => {
  const { reportViewerTimeoutMs, reportDateRangeLimit } = useFlags();
  const thresholdDays = (reportDateRangeLimit > 0) ? reportDateRangeLimit : 7;
  const viewerRef = useRef();
  const { token, refreshToken } = useManagedAuth();
  const { facilityID } = useCurrentFacility();
  const facilityIDs = useSelector((state) => state.entityScope.available) ?? [];
  const [ajaxInitialized, setAjaxInitialized] = useState(false);
  const currentUser = useSelector((state) => state.user, shallowEqual);
  const facilityGroupID = useSelector((state) => state.entityScope?.facilityGroupId);
  const enqueueSnackbar = useEnqueueSnackbar();
  const history = useHistory();
  const reportQueueService = new ReportQueueService(apiClient);
  const observerRef = useRef(null);

  const getReportUri = () => {
    let pairs = {};
    switch (reportName?.toLowerCase()) {
      case "validationstatement":
      case "onepassactivepasses":
        if (currentUser.UserType === USER_TYPES.ValidationAccount)
          pairs["userID"] = currentUser.UserID;
    }
    // this will either be all facilities in group OR a single facility ID:
    if(facilityGroupID && facilityIDs.length > 0) {
      pairs["facilityID"] = facilityIDs.map((f)=>f.id).join();
    } else {
      pairs["facilityID"] = facilityID;
    }
    pairs["facilityGroupID"] = facilityGroupID;
    pairs["currentFacilityID"] = facilityID;

    const params = new URLSearchParams(pairs);
    return encodeURI(`${reportName}?${params.toString()}`);
  };

  const renderControls = useCallback(() => {
    if (observerRef.current) return;

    const observer = new MutationObserver(() => {
      const resetButton = document.querySelector('.dxrdp-parameters-reset');
      const allScrollableContents = document.querySelectorAll('.dx-scrollable-content');
      if (!resetButton) return;

      let targetScrollableContent = null;

      for (const content of allScrollableContents) {
        if (content.contains(resetButton)) {
          targetScrollableContent = content;
          break;
        }
      }

      if (!targetScrollableContent) return;

      if (reportQueueV2Enabled) {
        const alreadyInjected = targetScrollableContent.querySelector('#react-v2-routing-controls');
        if (alreadyInjected) return;

        const reactContainer = document.createElement("div");
        reactContainer.id = "react-v2-routing-controls";
        reactContainer.className = "report-queue-button-container";

        ReactDOM.render(
          <ThemeProvider>
            <ReportV2RoutingWrapper
              reportName={reportName}
              currentUser={currentUser}
              reportQueueService={reportQueueService}
              enqueueSnackbar={enqueueSnackbar}
              history={history}
              thresholdDays={thresholdDays}
            />
          </ThemeProvider>,
          reactContainer
        );

        targetScrollableContent.appendChild(reactContainer);
      } else {
        const alreadyInjected = targetScrollableContent.querySelector('#react-queue-controls');
        if (alreadyInjected) return;

        const reactContainer = document.createElement("div");
        reactContainer.id = "react-queue-controls";
        reactContainer.className = "report-queue-button-container";

        ReactDOM.render(
          <ThemeProvider>
            <ReportQueueControlsWrapper
              reportName={reportName}
              currentUser={currentUser}
              reportQueueService={reportQueueService}
              enqueueSnackbar={enqueueSnackbar}
              history={history}
            />
          </ThemeProvider>,
          reactContainer
        );

        targetScrollableContent.appendChild(reactContainer);
      }

      observer.disconnect();
      observerRef.current = null;
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    observerRef.current = observer;
  }, [reportName, currentUser, reportQueueService, enqueueSnackbar, history, reportQueueV2Enabled, thresholdDays]);

  useEffect(() => {
    if (reportQueueEnabled || reportQueueV2Enabled) {
      renderControls();
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [renderControls, reportQueueEnabled, reportQueueV2Enabled]);

  useEffect(() => {
    if (!reportName || !ajaxInitialized || !token) {
      return;
    }

    if (reportQueueV2Enabled) {
      // V2: standard toolbar visible, ReportV2RoutingWrapper manages Submit visibility
      ko.applyBindings(
        {
          reportUrl: getReportUri(),
          requestOptions: {
            host: `${AppConfig.externalServices.baseURL}/reports/`,
            invokeAction: "DXXRDV",
          },
          callbacks: {
            BeforeRender: function(s, e) {
              ViewerTimeOut(reportViewerTimeoutMs ?? 300000);
            },
            CustomizeElements: function (s, e) {
              renderControls();
            }
          },
        },
        viewerRef.current
      );

      return () => ko.cleanNode(viewerRef.current);
    } else if (reportQueueEnabled) {
      ko.applyBindings(
        {
          reportUrl: getReportUri(),
          requestOptions: {
            host: `${AppConfig.externalServices.baseURL}/reports/`,
            invokeAction: "DXXRDV",
          },
          callbacks: {
            BeforeRender: function(s, e) {
              ViewerTimeOut(reportViewerTimeoutMs ?? 300000);
            },
            CustomizeElements: function (s, e) {
              renderControls();
            }
          },
        },
        viewerRef.current
      );

      return () => ko.cleanNode(viewerRef.current);
    } else {
      ko.applyBindings(
        {
          reportUrl: getReportUri(),
          requestOptions: {
            host: `${AppConfig.externalServices.baseURL}/reports/`,
            invokeAction: "DXXRDV",
          },
          callbacks: {
            BeforeRender: function(s, e) {
              ViewerTimeOut(reportViewerTimeoutMs ?? 300000);
            },
            OnExport: function(src, event) {
              event.FormData["access_token"] = token;
              event.RequestUrl = `${AppConfig.externalServices.baseURL}/reports/DXXRDV/export`;
            },
            CustomizeMenuActions: function(src, event) {
              // Get the page navigation actions and hide them.
              var actionPrint = event.GetById(
                ActionId.Print
              );
              if (actionPrint) actionPrint.visible = false;

              var actionPrintPage = event.GetById(
                ActionId.PrintPage
              );

              if (actionPrintPage) actionPrintPage.visible = false;
            },
          },
        },
        viewerRef.current
      );

      return () => ko.cleanNode(viewerRef.current);
    }
  }, [reportName, ajaxInitialized, token, reportQueueEnabled, reportQueueV2Enabled, viewerRef]);

  useEffect(() => {
    if (!token) {
      return;
    }

    ajaxSetup.ajaxSettings = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      error: function(jqXHR, textStatus, errorThrown) {
        if (jqXHR.status == 401) {
          refreshToken();
        }
      },
    };

    setAjaxInitialized(true);
  }, [token]);

  // Both V1 and V2 hide the DevExpress toolbar (Reset/Submit buttons).
  // V2 renders its own Material UI buttons that trigger DevExpress actions.
  const hideToolbar = reportQueueEnabled || reportQueueV2Enabled;

  return (
    <div className={`${className} ${hideToolbar ? "hide-toolbar" : ""}`}>
      <div
        className="viewport"
        ref={viewerRef}
        data-bind="dxReportViewer: $data"
      />
    </div>
  );
};

ReportRenderer.propTypes = {
  className: PropTypes.string,
  reportName: PropTypes.string.isRequired,
  reportQueueEnabled: PropTypes.bool,
  reportQueueV2Enabled: PropTypes.bool,
};

export default ReportRenderer;
